import React, { useState, useMemo } from 'react';
import { Table, Button, Text, Input, Row, Loading } from '@nextui-org/react';
import { Trash, Plus, Save, Undo, X, Send } from 'lucide-react';
import { commitWorkshopParticipantChanges, WorkshopUser } from '../../services/workshopService';
import toast, { Toaster } from 'react-hot-toast';

interface WorkshopParticipantsListProps {
  workshopId: string;
  users: WorkshopUser[];
  onRefreshData?: () => void;
}

export const WorkshopParticipantsList: React.FC<WorkshopParticipantsListProps> = ({ workshopId, users, onRefreshData }) => {
  const [newUserEmail, setNewUserEmail] = useState('');

  // Staging state
  const [pendingAdditions, setPendingAdditions] = useState<WorkshopUser[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const handleAddUser = () => {
    if (!newUserEmail) return;

    // Check if email already exists in users or pendingAdditions
    const emailExistsInUsers = users.some(u => u.email === newUserEmail);
    const emailExistsInPending = pendingAdditions.some(u => u.email === newUserEmail);

    if (emailExistsInUsers || emailExistsInPending) {
      toast.error("User with this email already exists or is staged.");
      return;
    }

    const roles = ['Developer', 'Business', 'UX'];
    const tempUser: WorkshopUser = {
      id: `temp-${Date.now()}`,
      name: newUserEmail.split('@')[0],
      email: newUserEmail,
      role: roles[Math.floor(Math.random() * roles.length)],
      group: Math.floor(Math.random() * 999) + 1
    };

    setPendingAdditions([...pendingAdditions, tempUser]);
    setNewUserEmail('');
    toast.success("User staged for addition");
  };

  const handleRemoveUser = (user: WorkshopUser) => {
    // If it's a pending addition, just remove it from the list
    if (pendingAdditions.some(u => u.id === user.id)) {
      setPendingAdditions(pendingAdditions.filter(u => u.id !== user.id));
      toast.success("Addition cancelled");
      return;
    }

    // If it's an existing user, add to pending removals
    const newRemovals = new Set(pendingRemovals);
    newRemovals.add(user.id);
    setPendingRemovals(newRemovals);
    toast.success("User staged for removal");
  };

  const handleUndoRemove = (userId: string) => {
    const newRemovals = new Set(pendingRemovals);
    newRemovals.delete(userId);
    setPendingRemovals(newRemovals);
    toast.success("Removal undone");
  };

  const handleSaveChanges = async () => {
    if (pendingAdditions.length === 0 && pendingRemovals.size === 0) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://dummyjson.com/c/3029-d29f-4014-9fb4');
      if (!response.ok) {
        throw new Error('Failed to validate changes');
      }

      await commitWorkshopParticipantChanges(
        workshopId,
        pendingAdditions.map(u => u.email),
        Array.from(pendingRemovals)
      );

      setPendingAdditions([]);
      setPendingRemovals(new Set());
      if (onRefreshData) {
        onRefreshData();
      }
      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Failed to save changes", error);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendInvite = async (user: WorkshopUser) => {
    try {
      const response = await fetch('https://dummyjson.com/c/3029-d29f-4014-9fb4');
      if (response.ok) {
        toast.success(`Invite sent to ${user.email}`);
      } else {
        toast.error(`Failed to send invite to ${user.email}`);
      }
    } catch (error) {
      console.error("Failed to send invite", error);
      toast.error(`Failed to send invite to ${user.email}`);
    }
  };

  const displayUsers = useMemo(() => {
    // Start with existing users Ensure users is an array
    const safeUsers = Array.isArray(users) ? users : [];
    const currentUsers = safeUsers.map(user => {
      const uniqueId = user.id || user.email;
      return {
        ...user,
        id: uniqueId,
        status: pendingRemovals.has(uniqueId) ? 'removing' : 'existing'
      };
    });

    // Add pending additions
    const newUsers = pendingAdditions.map(user => ({
      ...user,
      status: 'adding'
    }));

    return [...currentUsers, ...newUsers];
  }, [users, pendingAdditions, pendingRemovals]);

  const columns = [
    { name: "NAME", uid: "name" },
    { name: "EMAIL", uid: "email" },
    { name: "ROLE", uid: "role" },
    { name: "GROUP", uid: "group" },
    { name: "ACTIONS", uid: "actions" },
  ];

  const renderCell = (user: any, columnKey: React.Key) => {
    const cellValue = user[columnKey as keyof WorkshopUser];
    switch (columnKey) {
      case "name":
        return <Text css={{ textDecoration: user.status === 'removing' ? 'line-through' : 'none' }}>{user.name}</Text>;
      case "email":
        return <Text css={{ textDecoration: user.status === 'removing' ? 'line-through' : 'none' }}>{user.email}</Text>;
      case "role":
        return <Text css={{ textDecoration: user.status === 'removing' ? 'line-through' : 'none' }}>{user.role}</Text>;
      case "group":
        return <Text css={{ textDecoration: user.status === 'removing' ? 'line-through' : 'none' }}>{user.group}</Text>;
      case "actions":
        if (user.status === 'adding') {
          return (
            <Button auto light color="error" icon={<X size={18} />} onClick={() => handleRemoveUser(user)} title="Cancel Addition" />
          );
        }
        if (user.status === 'removing') {
          return (
            <Button auto light color="primary" icon={<Undo size={18} />} onClick={() => handleUndoRemove(user.id)} title="Undo Removal" />
          );
        }
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              auto
              light
              color="primary"
              icon={<Send size={16} />}
              onClick={() => handleSendInvite(user)}
              title="Send Invite"
            />
            <Button
              auto
              color="error"
              icon={<Trash size={16} />}
              onClick={() => handleRemoveUser(user)}
              title="Remove User"
            />
          </div>
        );
      default:
        return <Text>{String(cellValue)}</Text>;
    }
  };

  const hasPendingChanges = pendingAdditions.length > 0 || pendingRemovals.size > 0;

  return (
    <div>
      <Toaster />
      <Row align="center" justify="space-between" css={{ mb: "$5" }}>
        <Text h4>Workshop Participants</Text>
        {hasPendingChanges && (
          <Button
            auto
            color="success"
            icon={isSaving ? <Loading type="spinner" color="currentColor" size="sm" /> : <Save size={18} />}
            disabled={isSaving}
            onClick={handleSaveChanges}
          >
            Save Changes
          </Button>
        )}
      </Row>

      <Row align="center" css={{ mb: "$8" }}>
        <Input
          clearable
          bordered
          fullWidth
          color="primary"
          size="md"
          placeholder="User Email"
          value={newUserEmail}
          onChange={(e) => setNewUserEmail(e.target.value)}
          contentRight={
            <Button
              auto
              color="primary"
              onClick={handleAddUser}
              disabled={!newUserEmail}
              icon={<Plus size={20} />}
            >
              Add
            </Button>
          }
        />
      </Row>

      {false ? (
        <Row justify="center">
          <Loading />
        </Row>
      ) : (
        <Table
          aria-label="Workshop Participants List"
          selectionMode="none"
          css={{
            height: "auto",
            minWidth: "100%",
          }}
        >
          <Table.Header columns={columns}>
            {(column) => (
              <Table.Column key={column.uid} hideHeader={column.uid === "actions"} align={column.uid === "actions" ? "center" : "start"}>
                {column.name}
              </Table.Column>
            )}
          </Table.Header>
          <Table.Body items={displayUsers}>
            {(item: any) => (
              <Table.Row key={item.id || item.email || Math.random().toString()}>
                {(columnKey: React.Key) => (
                  <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>
                )}
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default WorkshopParticipantsList;
